import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Gift } from "lucide-react";
import {
  setCategoryItemsSource,
  clearCategoryFilters,
  reloedFunc,
} from "../../server/Product.js";
import {
  getAllCategory,
  getBySlug,
  setCurrentCategoryDispatch,
} from "../../server/Category";
import SubNavbar from "./SubNavbar.js";
import SubNavbarSkeleton from "./SubNavbarSkeleton.js";
import SmartScrollRow from "./SmartScrollRow.js";
import { getCategoryNavIconComponent } from "./categoryNavIcons.js";
import { useMediaQuery } from "../../hooks/useMediaQuery.js";
import useCategoryReorder from "./useCategoryReorder";

function findCategoryBySlug(categories, slug) {
  if (!categories) return undefined;
  for (const cat of categories) {
    if (cat.slug === slug) return cat;
    const found = findCategoryBySlug(cat.children, slug);
    if (found) return found;
  }
}

// Mobile drill-down (pills, breadcrumb, nav drawer) reads the nested category
// tree from Redux; depth 3 covers the navigable levels. Families are filtered
// out of drill-down, so they're excluded from the mobile load (smaller payload).
// Desktop only needs the top row (depth 1) — deeper levels come from getBySlug,
// so it must stay at 1 with families included.
const CATEGORY_TREE_DEPTH = 3;

const formatCategoryName = (name) => {
  const lines = name.split("|");

  return (
    <span style={{ lineHeight: "1.15" }}>
      {lines.map((line, index) => (
        <span className="nowrap-text" key={index}>
          {line.trim()}
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </span>
  );
};

const NAVIGATOR = ({ currentSubCategory, setPendingScrollSlug }) => {
  // ≤768px is mobile (matches $bp-tablet boundary). Desktop nav is not rendered there.
  const isMobile = useMediaQuery("(max-width: 768px)");
  const categories = useSelector((state) => state.category.categories);
  const toggleMenu = useSelector((state) => state.openCart.toggleMenu);
  const stationId = useSelector((state) => state.user.stationId);
  const items = useSelector((state) => state.displayProduct.itemsShow);
  const reloed = useSelector((state) => state.displayProduct.reloed);
  const isClientView = useSelector((state) => state.category.isClientView);
  const includeInactive = useSelector(
    (state) => state.category.includeInactive,
  );
  const role = useSelector((state) => state.user.role);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [, setChildren] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [orderedTopCategories, setOrderedTopCategories] = useState([]);

  const currentAbortController = useRef(null);
  const isSalesPageRef = useRef(false);

  const isClientViewRef = useRef(isClientView);
  const includeInactiveRef = useRef(includeInactive);

  const visibleTopCategories = useMemo(
    () =>
      (categories || []).filter(
        (cat) =>
          cat.status === "ACTIVE" &&
          cat.totalProducts > 0 &&
          (!includeInactive || !role ? cat.activeProducts > 0 : true),
      ),
    [categories, includeInactive, role],
  );

  useEffect(() => {
    setOrderedTopCategories(visibleTopCategories);
  }, [visibleTopCategories]);

  const {
    canReorder: canReorderTopLevel,
    handleDragStart: handleTopDragStart,
    handleDragOver: handleTopDragOver,
    handleDrop: handleTopDrop,
    handleDragEnd: handleTopDragEnd,
    suppressClickRef,
  } = useCategoryReorder({
    items: orderedTopCategories,
    setItems: setOrderedTopCategories,
    enabled: Boolean(role && stationId),
    stationId,
    isClientView: isClientViewRef.current,
    includeInactive: includeInactiveRef.current,
    refreshCategorySlug:
      pathname.startsWith("/category/") && pathname.split("/")[2]
        ? pathname.split("/")[2]
        : null,
    optimisticParentSlug: null,
  });

  const handleCategoryClick = useCallback(
    (slug) => {
      setActiveSlug(slug);
      dispatch(clearCategoryFilters({ resetSource: true }));
      dispatch(reloedFunc());
      if (currentAbortController.current) {
        currentAbortController.current.abort();
      }

      const abortController = new AbortController();
      currentAbortController.current = abortController;
      setChildren(null);

      getBySlug(
        stationId,
        slug,
        isClientViewRef.current,
        includeInactiveRef.current,
        {
          signal: abortController.signal,
        },
      )
        .then((res) => {
          if (isSalesPageRef.current) return;

          if (!abortController.signal.aborted) {
            dispatch(setCategoryItemsSource(res));
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    },
    [dispatch, stationId],
  );

  useEffect(() => {
    isSalesPageRef.current = pathname === "/sales";
  }, [pathname]);

  useEffect(() => {
    if (pathname === "/sales" && currentAbortController.current) {
      currentAbortController.current.abort();
    }
  }, [pathname]);

  useEffect(() => {
    if (localStorage.getItem("jwtToken"))
      dispatch(
        getAllCategory(
          stationId,
          null,
          isMobile ? CATEGORY_TREE_DEPTH : 1,
          isMobile ? false : true, // includeFamilies — off on mobile drill-down
          false,
        ),
      );
  }, [dispatch, stationId, isMobile]);

  useEffect(() => {
    if (pathname.startsWith("/category/")) {
      const splited = pathname.split("/");
      const slug = splited[2];
      setCurrentCategory(slug);
      setActiveSlug(slug);
      if (
        currentCategory !== slug &&
        items?.slug !== slug &&
        splited.length === 3
      ) {
        handleCategoryClick(slug);
      }
      const category = findCategoryBySlug(categories, slug);
      setChildren(category);
      dispatch(setCurrentCategoryDispatch(category));
    } else {
      setCurrentCategory(null);
      setActiveSlug(null);
      setChildren(null);
    }
  }, [
    pathname,
    categories,
    currentCategory,
    items?.slug,
    handleCategoryClick,
    dispatch,
  ]);

  useEffect(() => {
    if (pathname.startsWith("/category/")) {
      const splited = pathname.split("/");
      const slug = splited[2];
      if (splited.length === 3) {
        handleCategoryClick(slug);
        setCurrentCategory(slug);
      }
    }
    // Re-fetch when station changes; full deps would duplicate the category effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationId]);

  useEffect(() => {
    if (role) isClientViewRef.current = isClientView;
    else isClientViewRef.current = true;
  }, [isClientView, role]);

  useEffect(() => {
    if (role) includeInactiveRef.current = includeInactive;
    else includeInactiveRef.current = false;
  }, [includeInactive, role]);

  // Mobile uses MobileCategoryPills + breadcrumb instead — render no desktop nav.
  if (isMobile) return null;

  return (
    <div className="navigator">
      <div className="navigator__stack">
        <nav className={toggleMenu ? "active main" : "main"}>
          <SmartScrollRow rtl trackClassName="navigator__scroll-track">
            <div className="item-sale">
              <NavLink
                className={
                  pathname === "/sales"
                    ? "item-sale-link item-sale-link--active"
                    : "item-sale-link"
                }
                to="/sales"
                aria-label="מבצעים"
              >
                <span className="category-nav-item-inner item-sale-chip">
                  <span className="item-sale-chip__ring" aria-hidden />
                  <span
                    className="item-sale-chip__spark item-sale-chip__spark--a"
                    aria-hidden
                  />
                  <span
                    className="item-sale-chip__spark item-sale-chip__spark--b"
                    aria-hidden
                  />
                  <span className="category-nav-item-icon" aria-hidden>
                    <Gift
                      className="category-nav-svg"
                      size={22}
                      strokeWidth={1.65}
                      aria-hidden
                    />
                  </span>
                  <span className="category-nav-item-label">מבצעים</span>
                </span>
              </NavLink>
            </div>
            {orderedTopCategories &&
              orderedTopCategories.length > 0 &&
              orderedTopCategories.map((element, index) => {
                const CategoryIcon = getCategoryNavIconComponent(
                  element.slug,
                  element.name,
                  index,
                );
                return (
                  <a
                    key={element.slug || index}
                    href="/"
                    className={
                      element.slug === currentCategory ||
                      element.slug === activeSlug
                        ? "item item-active"
                        : "item"
                    }
                    draggable={canReorderTopLevel}
                    onDragStart={() => handleTopDragStart(element.slug)}
                    onDragOver={handleTopDragOver}
                    onDrop={(e) => handleTopDrop(e, element.slug)}
                    onDragEnd={handleTopDragEnd}
                    onClick={(e) => {
                      if (suppressClickRef.current) {
                        e.preventDefault();
                        return;
                      }
                      e.preventDefault();
                      setActiveSlug(element.slug);
                      navigate(`/category/${element.slug}`);
                    }}
                  >
                    <span className="category-nav-item-inner">
                      <span className="category-nav-item-icon" aria-hidden>
                        <CategoryIcon
                          className="category-nav-svg"
                          size={22}
                          strokeWidth={1.65}
                          aria-hidden
                        />
                      </span>
                      <span className="category-nav-item-label">
                        {formatCategoryName(element.name)}
                      </span>
                    </span>
                  </a>
                );
              })}
          </SmartScrollRow>
        </nav>
        {currentCategory &&
          !pathname.startsWith("/cart") &&
          (reloed && pathname.startsWith("/category") && !items?.children ? (
            <SubNavbarSkeleton />
          ) : items?.children ? (
            <SubNavbar
              fatherSlug={currentCategory}
              currentSubCategory={currentSubCategory}
              currentCategories={items}
              setPendingScrollSlug={setPendingScrollSlug}
            />
          ) : null)}
      </div>
    </div>
  );
};

export default NAVIGATOR;
